import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Typography, Grid, Card, CardContent, Box, Button, Chip,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import CategoryIcon from '@mui/icons-material/Category';
import AddIcon from '@mui/icons-material/Add';
import { listArticles, listCategories } from '../../utils/api';
import { Article, Category } from '../../utils/types';

const Dashboard: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [arts, cats] = await Promise.all([listArticles(), listCategories()]);
        setArticles(arts);
        setCategories(cats);
      } catch (err) {
        console.error('Failed to load dashboard data', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const publishedCount = articles.filter(a => a.status === 'published').length;
  const draftCount = articles.filter(a => a.status === 'draft').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>Dashboard</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/admin/articles/new"
        >
          New Article
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ArticleIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="h3" fontWeight={700} sx={{ mt: 1 }}>{articles.length}</Typography>
              <Typography color="text.secondary">Total Articles</Typography>
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Chip label={`${publishedCount} published`} size="small" color="success" variant="outlined" />
                <Chip label={`${draftCount} drafts`} size="small" variant="outlined" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CategoryIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="h3" fontWeight={700} sx={{ mt: 1 }}>{categories.length}</Typography>
              <Typography color="text.secondary">Categories</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h5" fontWeight={600} gutterBottom>Recent Articles</Typography>
      {articles.slice(0, 5).map((article) => (
        <Card key={article.id} sx={{ mb: 1 }}>
          <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box>
              <Typography fontWeight={600}>{article.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {article.category} &middot; {new Date(article.updatedAt).toLocaleDateString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={article.status}
                size="small"
                color={article.status === 'published' ? 'success' : 'default'}
              />
              <Button size="small" component={RouterLink} to={`/admin/articles/${article.id}`}>
                Edit
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default Dashboard;
